import cn from 'classnames'
import { isEmpty } from 'lodash'

import React, { useEffect } from 'react'

import {
  SectionHeader,
  PricingCard,
  Loader,
  IconNew,
  ButtonNew,
  PricingCardSecondary
} from '../../../storybook'

import { PRICING } from '../../../utils/common'
import { getUrl } from '../../../utils/urls'
import { SiteDataContext } from '../../page-wrapper'

import { useMicroCopyData } from '@/src/context/MicroCopyContext'
import Image from 'next/image'
import RichText from '../../rich-text'
import { BUTTON_VARIANT } from '../../../storybook/ButtonNew/ButtonNew'
import { getMicroCopy } from '../../../utils/microcopy'
import { useNavigationData } from '@/src/context/NavigationContext'

export const BILLING_TIME_MONTHLY = PRICING.BILLING_TIME_MONTHLY
export const BILLING_TIME_ANNUALLY = PRICING.BILLING_TIME_ANNUALLY
export const PAY_MONTHLY_KEY = PRICING.PAY_MONTHLY_KEY
export const PAY_ANNUALLY_KEY = PRICING.PAY_ANNUALLY_KEY

const BlockPricingCards = ({ data }) => {
  const { market, isTextRtl, lang } = React.useContext(SiteDataContext)
  const { tiers } = market.pricing
  const { slugPrefix } = data.extra
  const { navigationItems } = useNavigationData()
  const microCopyData = useMicroCopyData()

  if (isEmpty(tiers)) {
    return null
  }

  const [cards, setCards] = React.useState([])
  const [cardsShowMore, setCardsShowMore] = React.useState(false)

  const getExtraInfoKey = (price) => {
    if (!price) return

    const component = (
      <>
        <strong>
          {getMicroCopy({ key: 'from', fallback: 'From', data: microCopyData[lang] })}{' '}
          {market?.currencySymbol ? market.currencySymbol : '$'}
          {price}
        </strong>
        /{getMicroCopy({ key: 'month', data: microCopyData[lang] })}
      </>
    )

    return component
  }

  useEffect(() => {
    function extendCardsByPrices() {
      return data.pricingCards.map((card) => {
        const relatedPrice = tiers.find((tier) => card.pricingTier === tier.tierTitle)

        if (relatedPrice) {
          return {
            ...relatedPrice,
            ...card
          }
        }

        return null
      })
    }

    setCards(extendCardsByPrices)
  }, [])

  const isPricingCards = !!cards?.length

  return (
    <section
      className={cn('l-section c-pricing-cards l-container:12/12to:viewport-9', {
        'c-pricing-cards--rtl': isTextRtl
      })}
      dir={isTextRtl ? 'rtl' : 'ltr'}
    >
      <SectionHeader
        title={data.title || ''}
        headingType="h1"
        align="center"
        classes="c-pricing-cards__header l-container:10/12 l-container:1/12@to:viewport-7"
      />

      {isPricingCards ? (
        <>
          <div className="c-pricing-cards__bottom-layout l-container:12/12 l-container--default-spacing">
            <div>
              {<RichText data={data.description} classes={'c-pricing-cards__cards-title'} />}
            </div>
            <div className="c-pricing-cards__cards-wrapper">
              {data?.pricingCards.map((card) => (
                <PricingCard
                  key={card.id}
                  card={card}
                  slugPrefix={slugPrefix}
                  microCopy={microCopyData}
                  showMore={cardsShowMore}
                  toggleShowMore={setCardsShowMore}
                />
              ))}
            </div>
          </div>
        </>
      ) : (
        <section className="l-section">
          <div className="l-container:12/12 l-container--default-spacing">
            <Loader />
          </div>
        </section>
      )}
      {data.secondaryCards && (
        <div className="c-pricing-cards__bottom-layout c-pricing-cards__secondary-container l-container:12/12 l-container--default-spacing">
          {data.secondaryCards.map((card) => (
            <PricingCardSecondary
              card={card}
              key={`secondary-card-${card.id}`}
              slugPrefix={slugPrefix}
            />
          ))}
        </div>
      )}
      {data.moreSolutions && (
        <div className="c-pricing-cards__more-solutions l-container:12/12 l-container--default-spacing">
          <p className="c-pricing-cards__more-solutions__title">{data.moreSolutions.title}</p>
          {data.moreSolutions.featureCards.map((card, i) => {
            return (
              <div key={`${card.id}-${i}`} className="c-pricing-cards__more-solutions__card">
                <div className="c-pricing-cards__more-solutions__card__info-container">
                  {card.image ? (
                    <div className="c-pricing-cards__more-solutions__card__info-container__image">
                      <Image
                        src={card.image.url}
                        width={32}
                        height={32}
                        alt={card.title}
                        style={{
                          objectFit: 'contain'
                        }}
                      />
                    </div>
                  ) : card.icon ? (
                    <IconNew icon={card.icon} width={32} height={32} />
                  ) : null}
                  <div className="c-pricing-cards__more-solutions__card__info-container__text-container">
                    <div className="c-pricing-cards__more-solutions__card__title">{card.title}</div>
                    <div className="c-pricing-cards__more-solutions__card__description">
                      <RichText data={card.description} />
                    </div>
                  </div>
                </div>
                <div className="c-pricing-cards__more-solutions__card__price-container">
                  {card.price ? (
                    <div className="c-pricing-cards__more-solutions__card__extraInfo">
                      {getExtraInfoKey(card.price)}
                    </div>
                  ) : null}
                  {card.action && (
                    <ButtonNew
                      variant={BUTTON_VARIANT.PRIMARY}
                      url={getUrl({
                        navItem: card.action,
                        slugPrefix: slugPrefix,
                        navigationItems: navigationItems
                      })}
                      size={'md'}
                    >
                      {card.action.title}
                    </ButtonNew>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}

export default BlockPricingCards
